import apiClient from './api.client';
import { API } from '../constants/api';
import type {
  PublicEventMapPin,
  PublicEventDetail,
  PlaceSearchResult,
  CreatePublicEventPayload,
  UpdatePublicEventPayload,
  EventAttendee,
} from '../types/public_event.types';

export async function createPublicEvent(payload: CreatePublicEventPayload): Promise<PublicEventDetail> {
  const res = await apiClient.post<{ data: PublicEventDetail }>(API.PUBLIC_EVENTS, payload);
  return res.data.data;
}

export async function getPublicEventById(id: string): Promise<PublicEventDetail> {
  const res = await apiClient.get<{ data: PublicEventDetail }>(API.PUBLIC_EVENT_BY_ID(id));
  return res.data.data;
}

export async function updatePublicEvent(id: string, payload: UpdatePublicEventPayload): Promise<PublicEventDetail> {
  const res = await apiClient.patch<{ data: PublicEventDetail }>(API.PUBLIC_EVENT_BY_ID(id), payload);
  return res.data.data;
}

export async function getUpcomingEventsByVenue(venueId: string): Promise<PublicEventMapPin[]> {
  const res = await apiClient.get<{ data: PublicEventMapPin[] }>(
    `/public-events/venue/${venueId}/upcoming`,
  );
  return res.data.data;
}

export async function searchPlacesForEvent(
  q: string,
  lat?: number,
  lng?: number,
): Promise<PlaceSearchResult[]> {
  const params = new URLSearchParams({ q });
  if (lat !== undefined) params.set('lat', String(lat));
  if (lng !== undefined) params.set('lng', String(lng));
  const res = await apiClient.get<{ data: PlaceSearchResult[] }>(
    `${API.PUBLIC_EVENTS_PLACES_SEARCH}?${params}`,
  );
  return res.data.data;
}

export async function matchEvent(eventId: string): Promise<{ matched: boolean }> {
  const res = await apiClient.post<{ data: { matched: boolean } }>(API.PUBLIC_EVENT_MATCH(eventId));
  return res.data.data;
}

export async function unmatchEvent(eventId: string): Promise<{ removed: boolean }> {
  const res = await apiClient.delete<{ data: { removed: boolean } }>(API.PUBLIC_EVENT_MATCH(eventId));
  return res.data.data;
}

/** Returns the going attendees for a public event, sorted by Trust Score desc. */
export async function getEventAttendees(eventId: string): Promise<EventAttendee[]> {
  const res = await apiClient.get<{ data: EventAttendee[] }>(API.PUBLIC_EVENT_ATTENDEES(eventId));
  return res.data.data;
}

// ── Cover image upload (Sprint 5) ────────────────────────────────────────────

/**
 * 1. POST /public-events/cover-image-url for a presigned R2 PUT URL.
 * 2. PUT the file directly to R2 — backend never touches the bytes.
 * 3. Return the public URL the caller stores as `coverImageUrl` on the event.
 */
export async function uploadCoverImage(file: File): Promise<string> {
  const contentType = file.type || 'image/jpeg';
  const presigned = await apiClient.post<{
    data: { uploadUrl: string; publicUrl: string; key: string };
  }>(API.PUBLIC_EVENT_COVER_UPLOAD, { contentType });

  const { uploadUrl, publicUrl } = presigned.data.data;
  const putRes = await fetch(uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': contentType },
    body: file,
  });
  if (!putRes.ok) throw new Error(`Cover image upload failed (${putRes.status})`);
  return publicUrl;
}
